import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  Logo,
  NonProdEnvironmentIcon,
  ProdEnvironmentIcon,
  SkeletonLoader,
  useGetEnvironments,
  useGetIntegration,
  useUpdateIntegrationEnvironment,
} from '../../../main';
import { Select } from '../../atoms/Select/Select';
import { EnvironmentSuggestion } from '../../main';

import { useTranslation } from 'react-i18next';

export type IntegrationProps = {
  /** `integrationId` */
  id: string;
  /** When false, the environment suggestion block (Dismiss / Apply suggestion) is hidden. Defaults to true. */
  showSuggestion?: boolean;

  /** When true, the environment dropdown is disabled and cannot be changed. Defaults to false. */
  disableSelection?: boolean;
};

/**
 * IntegrationCard displays a Plerion integration
 *
 * Example:
 * ```tsx
 *   <IntegrationCard id={integration.id} />
 * ```
 */
export const IntegrationCard = ({
  id: integrationId,
  showSuggestion = true,
  disableSelection = false,
}: IntegrationProps) => {
  const { t } = useTranslation('designSystem');
  const { data: integration, status } = useGetIntegration(integrationId);
  const { data: environments, isLoading: isEnvironmentsLoading } =
    useGetEnvironments();
  const { mutateAsync: updateEnvironment } = useUpdateIntegrationEnvironment();
  const { environment, name, provider, type } = integration ?? {};
  const [selectedEnvironment, setSelectedEnvironment] = useState(
    environment?.dimension?.value || '',
  );

  useEffect(() => {
    setSelectedEnvironment(environment?.dimension?.value || '');
  }, [environment?.dimension?.value]);

  const handleEnvironmentChange = async (value: string) => {
    const id = toast.loading(t('updatingEnvironment'));
    try {
      const environmentId =
        environments?.find(({ dimension }) => dimension?.value === value)
          ?.assetGroupId ?? '';
      setSelectedEnvironment(value);
      await updateEnvironment({ integrationId, environmentId });
      toast.success(t('environmentSuccessfullyUpdated'), { id });
    } catch {
      toast.error(t('failedToSetEnvironmentForIntegration'), { id });
    }
  };

  const showEnvSection =
    provider &&
    ['AWS', 'Azure', 'GCP', 'Kubernetes'].includes(provider as string);

  return (
    <div className="w-full">
      <SkeletonLoader status={status}>
        <div className="border-container-border rounded-card flex w-full flex-col overflow-hidden border">
          {/* Fixed column widths so "Current environment" and suggestion align across all cards (debrief: limit card width, align dropdown in column) */}
          <div className="flex w-full flex-row flex-nowrap items-center gap-4 overflow-x-auto p-4">
            <div className="flex w-64 shrink-0 items-center gap-2.5">
              {type && <Logo name={type} />}
              <div className="min-w-0 flex-1 overflow-hidden">
                <Link
                  aria-label={t('common:name')}
                  className="hyperlink block truncate"
                  title={name ?? t('common:name')}
                  to={`/settings/integration/${integrationId}`}
                >
                  {name}
                </Link>
              </div>
            </div>
            {showEnvSection && (
              <>
                <div className="flex w-48 shrink-0 flex-col gap-1">
                  <span className="text-xs">{t('currentEnvironment')}</span>
                  <Select
                    className="w-full"
                    isFixedPositionOptions
                    disabled={
                      disableSelection ||
                      isEnvironmentsLoading ||
                      (environments && environments.length === 0)
                    }
                    options={[
                      {
                        value: 'production',
                        label: (
                          <span className="flex items-center gap-1">
                            <ProdEnvironmentIcon className="h-4 w-4" />
                            {t('production')}
                          </span>
                        ),
                      },
                      {
                        value: 'non-production',
                        label: (
                          <span className="flex items-center gap-1">
                            <NonProdEnvironmentIcon className="h-4 w-4" />
                            {t('nonProduction')}
                          </span>
                        ),
                      },
                      {
                        value: '',
                        label: (
                          <span className="flex items-center gap-1">
                            {t('notSet')}
                          </span>
                        ),
                      },
                    ]}
                    onSelect={({ value }) => handleEnvironmentChange(value)}
                    value={selectedEnvironment}
                  />
                </div>

                {!selectedEnvironment && (
                  <div className="min-w-0 shrink-0">
                    <EnvironmentSuggestion
                      integrationId={integrationId}
                      currentEnvironmentValue={selectedEnvironment}
                      showSuggestion={showSuggestion}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </SkeletonLoader>
    </div>
  );
};
